import { z } from 'zod/v3';
import { CocoEvent } from '@cotera/client/app/etc';
import { ok } from 'neverthrow';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { AgentModel } from './agent.model';

type Params = {
  model: string;
};

export class ChangeAgentModelAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Change Agent Model';
  description = 'Change the LLM model for the agent';
  inputSchema = z.object({
    model: z.string(),
  });
  discrete = true;

  private _lastAgentId: string | null = null;
  private _lastFromModel: string | null = null;

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(AgentModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const agent = context.targetedModelOfType(AgentModel);

    if (agent === null) {
      return ok({});
    }

    this._lastFromModel = agent.modelWatchable.snapshot();
    this._lastAgentId = agent.agentId;

    agent.modelWatchable.set(params.model);
    agent.reconcileHasUnsavedFromResourceSnapshot();

    return ok({});
  }

  track({
    payload,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    if (this._lastAgentId === null) {
      return null;
    }
    return {
      event: CocoEvent.AgentModelChanged,
      properties: {
        agentId: this._lastAgentId,
        fromModel: this._lastFromModel,
        toModel: payload.model,
      },
    };
  }
}
